import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import {
  CommentDto,
  Country,
  CountryAgreement,
  CountryPartyType
} from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";
import { PartyRows } from "../../parties";
import {
  selectedPartyTypes,
  syncPartyRows,
  updatePartyRow
} from "../../parties/utils";
import { ClearContractCaseField } from "./clearContractCaseField";

type Props = {
  specificCountry: Country;
};

type FormData = {
  signOffNote: string;
  comments: CommentDto[];
};

const emptyAgreement = (partyType: CountryPartyType): CountryAgreement => ({
  partyType,
  reviewStatus: "",
  assignee: "",
  clearContractUrl: ""
});

/**
 * One review per party Market Access marked needed, each with its own reviewer and its own
 * ClearContract case. Unlike the counterparty and broker Legal dialogs there is no single agreement
 * review field, and the assignee lives on the row rather than on the lifecycle. A party that needs
 * no agreement is marked not needed on its own row, by Legal.
 */
export const Legal: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.legalLifecycle;
  const marketAccess = specificCountry.marketAccessLifecycle;

  const [formData, setFormData] = useState<FormData>({
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const [agreements, setAgreements] = useState<CountryAgreement[]>(() =>
    syncPartyRows(
      lifecycle?.agreements,
      selectedPartyTypes(marketAccess),
      emptyAgreement
    )
  );

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const extraSlice = () => ({ agreements });

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "legalLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const legalAssignees = useCountryAssignees("legal");

  const open = location.pathname.includes("legal");

  const canComplete =
    agreements.length > 0 &&
    agreements.every((agreement) => Boolean(agreement.reviewStatus));

  const update = (
    partyType: CountryPartyType,
    patch: Partial<CountryAgreement>
  ) => setAgreements((prev) => updatePartyRow(prev, partyType, patch));

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Legal"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(extraSlice()),
        onComplete: () => handleComplete(extraSlice()),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <PartyRows
          title="Agreement reviews"
          rows={agreements}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked any party as needed yet."
          renderRow={(agreement) => {
            const partyType = agreement.partyType as CountryPartyType;

            return (
              <>
                <Box display="flex" gap={2}>
                  <FormControl size="medium" required sx={{ flex: 1 }}>
                    <InputLabel>Review status</InputLabel>
                    <Select
                      label="Review status"
                      value={agreement.reviewStatus || ""}
                      onChange={(e) =>
                        update(partyType, { reviewStatus: e.target.value })
                      }
                    >
                      <MenuItem value="initiated">Initiated</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="not_needed">Not needed</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={legalAssignees.data || []}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.email})`
                    }
                    value={
                      legalAssignees.data?.find(
                        (a) => a.email === agreement.assignee
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      update(partyType, { assignee: newValue?.email || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Assignee" size="medium" />
                    )}
                  />
                </Box>

                <ClearContractCaseField
                  value={agreement.clearContractUrl || ""}
                  onChange={(value) =>
                    update(partyType, { clearContractUrl: value })
                  }
                />
              </>
            );
          }}
        />

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
